/* LocaleSettings — Firm Branding tab
   Fields: timezone (IANA grouped), date format, time format, default currency.
   Saves all four keys in a single PUT. */
import { useEffect, useState } from 'react';
import css from '../../../AdminPage.module.css';
import { useFirmBranding, useUpdateFirmBranding } from '../../../../../hooks/useAdmin';
import { useToast } from '../../../../../hooks/useToast';

// ─── Static option lists ──────────────────────────────────────────────────────

export const TIMEZONE_GROUPS: Record<string, string[]> = {
  UTC: ['UTC'],
  Africa: ['Africa/Cairo', 'Africa/Johannesburg', 'Africa/Lagos'],
  'America / Atlantic': [
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'America/New_York',
    'America/Sao_Paulo',
    'America/Toronto',
    'America/Vancouver',
    'Atlantic/Reykjavik',
  ],
  Asia: [
    'Asia/Dubai',
    'Asia/Hong_Kong',
    'Asia/Kolkata',
    'Asia/Seoul',
    'Asia/Shanghai',
    'Asia/Singapore',
    'Asia/Tokyo',
  ],
  Australia: ['Australia/Melbourne', 'Australia/Perth', 'Australia/Sydney'],
  Europe: [
    'Europe/Amsterdam',
    'Europe/Athens',
    'Europe/Berlin',
    'Europe/Brussels',
    'Europe/Budapest',
    'Europe/Copenhagen',
    'Europe/Dublin',
    'Europe/Helsinki',
    'Europe/Lisbon',
    'Europe/London',
    'Europe/Madrid',
    'Europe/Moscow',
    'Europe/Oslo',
    'Europe/Paris',
    'Europe/Prague',
    'Europe/Rome',
    'Europe/Stockholm',
    'Europe/Warsaw',
    'Europe/Zurich',
  ],
  Pacific: ['Pacific/Auckland', 'Pacific/Honolulu'],
};

export const DATE_FORMATS = [
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY  (31/12/2026)' },
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY  (12/31/2026)' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD  (2026-12-31)' },
];

export const TIME_FORMATS = [
  { value: '24h', label: '24-hour  (14:30)' },
  { value: '12h', label: '12-hour  (2:30 PM)' },
];

export const CURRENCIES = [
  { value: 'GBP', label: 'GBP — British Pound (£)' },
  { value: 'EUR', label: 'EUR — Euro (€)' },
  { value: 'USD', label: 'USD — US Dollar ($)' },
  { value: 'CAD', label: 'CAD — Canadian Dollar' },
  { value: 'AUD', label: 'AUD — Australian Dollar' },
  { value: 'SGD', label: 'SGD — Singapore Dollar' },
  { value: 'ZAR', label: 'ZAR — South African Rand' },
];

// ─── Component ────────────────────────────────────────────────────────────────

const KEYS = ['timezone', 'date_format', 'time_format', 'default_currency'] as const;
type Key = (typeof KEYS)[number];
type Draft = Record<Key, string>;

const DEFAULTS: Draft = {
  timezone: 'Europe/London',
  date_format: 'DD/MM/YYYY',
  time_format: '24h',
  default_currency: 'GBP',
};

export default function LocaleSettings() {
  const { data: branding, isLoading } = useFirmBranding();
  const update = useUpdateFirmBranding();
  const { showToast } = useToast();

  const [draft, setDraft] = useState<Draft>(DEFAULTS);
  const [original, setOriginal] = useState<Draft>(DEFAULTS);

  useEffect(() => {
    if (!branding) return;
    const seeded: Draft = {} as Draft;
    for (const k of KEYS) seeded[k] = branding[k] ?? DEFAULTS[k];
    setDraft(seeded);
    setOriginal(seeded);
  }, [branding]);

  function set(key: Key, value: string) {
    setDraft((prev) => ({ ...prev, [key]: value }));
  }

  const dirty = KEYS.some((k) => draft[k] !== original[k]);

  async function handleSave() {
    try {
      await update.mutateAsync(KEYS.map((k) => ({ key: k, value: draft[k] })));
      showToast('Locale settings saved', 'success');
      setOriginal({ ...draft });
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { error?: string } } })?.response?.data?.error;
      showToast(msg || 'Failed to save locale settings', 'error');
    }
  }

  if (isLoading) {
    return (
      <div className={css.spinner}>
        <div className={css.spinnerDot} />
        <div className={css.spinnerDot} />
        <div className={css.spinnerDot} />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 560 }}>
      <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--gray-600)', marginBottom: 24 }}>
        Org-wide defaults for dates, times, timezone, and currency. Individual users can override
        these in their personal preferences unless enforcement is enabled.
      </p>

      <div className={css.formGroup}>
        <label className={css.formLabel}>Timezone</label>
        <select
          data-testid="select-timezone"
          className={css.formSelect}
          value={draft.timezone}
          onChange={(e) => set('timezone', e.target.value)}
        >
          {Object.entries(TIMEZONE_GROUPS).map(([group, zones]) => (
            <optgroup key={group} label={group}>
              {zones.map((z) => (
                <option key={z} value={z}>
                  {z}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>

      <div className={css.grid2}>
        <div className={css.formGroup}>
          <label className={css.formLabel}>Date Format</label>
          <select
            data-testid="select-date_format"
            className={css.formSelect}
            value={draft.date_format}
            onChange={(e) => set('date_format', e.target.value)}
          >
            {DATE_FORMATS.map((f) => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
          </select>
        </div>

        <div className={css.formGroup}>
          <label className={css.formLabel}>Time Format</label>
          <select
            data-testid="select-time_format"
            className={css.formSelect}
            value={draft.time_format}
            onChange={(e) => set('time_format', e.target.value)}
          >
            {TIME_FORMATS.map((f) => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className={css.formGroup}>
        <label className={css.formLabel}>Default Currency</label>
        <select
          data-testid="select-default_currency"
          className={css.formSelect}
          value={draft.default_currency}
          onChange={(e) => set('default_currency', e.target.value)}
        >
          {CURRENCIES.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>
        <span
          style={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--gray-500)',
            marginTop: 4,
            display: 'block',
          }}
        >
          Used as the default currency across billing, invoices, and retainers
        </span>
      </div>

      <div style={{ marginTop: 8 }}>
        <button
          data-testid="btn-save"
          className={css.btnPrimary}
          onClick={handleSave}
          disabled={!dirty || update.isPending}
        >
          {update.isPending ? 'Saving…' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}
